import moment from 'moment';

import { BaseService, PaginatedResponse } from '../services/_base';
import { AxiosResponse } from 'axios';

import { retrieveSetting } from '@/globals/local-settings';

import { getLanguage, i18n } from '@/i18n/i18n';


const fractionDigits = new Map([
    ["seconds", 4],
    ["minutes", 2],
    ["hours", 0],
]);

function localizeMoment(value: string, format: string) {
    return moment(value).locale(i18n.global.locale).format(format);
}

function getNumberFormat() {
    const language = getLanguage(i18n.global.locale);
    return language?.numberFormat || 'de-DE';
}

function getTextColor(backgroundColor: string): string {
    const color = backgroundColor.replace('#', '');
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? '#000000' : '#FFFFFF';
}

function formatDurationV2(rawSeconds: number, precision: 'auto' | 'long' | 'short' = 'auto'): string {
    const h = i18n.global.t('message.hour_short');
    const min = i18n.global.t('message.minute_short');
    const s = i18n.global.t('message.second_short');

    if (rawSeconds == 0) return `0 ${h}`;

    const prefix = rawSeconds < 0 ? '- ' : '';
    rawSeconds = Math.abs(rawSeconds);

    const seconds = Math.floor(rawSeconds % 60);
    const minutes = Math.floor(rawSeconds / 60) % 60;
    const hours = Math.floor(rawSeconds / 3600);

    const components = [];
    if (hours > 0) components.push(`${hours} ${h}`);
    if (minutes > 0) components.push(`${minutes} ${min}`);
    if (seconds > 0) components.push(`${seconds} ${s}`);

    if (components.length == 0) return `0 ${s}`;
    if (precision == 'long') return prefix + components.join(' ');
    if (precision == 'short') return prefix + components.slice(0, 1).join(' ');
    return prefix + components.slice(0, 2).join(' ');
}

function formatDurationWithFormat(rawSeconds: number, precision="minutes", autoTrim = true): string {
    const roundedSeconds = Math.round(rawSeconds);
    const seconds = Math.abs(roundedSeconds);

    const format = retrieveSetting('decimalFormat');
    let result = "";
    if (format == 'decimal') {
        const fraction = fractionDigits.get(precision);
        result = (seconds / 3600).toLocaleString(getNumberFormat(), { minimumFractionDigits: fraction, maximumFractionDigits: fraction });
    } else {
        if (precision == "hours") {
            result = String(Math.round(seconds / 3600));
        }
        else if (precision == "minutes") {
            const minutes = Math.round(seconds / 60);
            result = String(Math.floor(minutes / 60)) + ":" + String(minutes % 60).padStart(2, '0');
        }
        else {
            const minutes = Math.floor(seconds / 60);
            result = String(Math.floor(minutes / 60)) + ":" + String(minutes % 60).padStart(2, '0') + ":" + String(seconds % 60).padStart(2, '0');
        }
    }

    if (!(roundedSeconds == seconds || result == '0:00' || result == '0,00')) {
        result = "-" + result
    }

    if (autoTrim) {
        while (result.slice(-3) == ":00" || result.slice(-3) == ",00") {
            result = result.slice(0, -3);
        }
    }

    return result
}

const formatDuration = async function (rawSeconds: number, precision = "minutes", autoTrim = true): Promise<string> {
    return formatDurationWithFormat(rawSeconds, precision, autoTrim);
}

const timedelta = function(dateEarlier: string, dateLater: string): number {
    return moment(dateLater).diff(moment(dateEarlier)) / 1000
}

const formatDifference = function(dateEarlier: string, dateLater: string, precision="minutes", autoTrim=true): Promise<string> {
    return formatDuration(timedelta(dateEarlier, dateLater), precision, autoTrim);
}

const formatDifferenceV2 = function (dateEarlier: string, dateLater: string): string {
    return formatDurationV2(timedelta(dateEarlier, dateLater));
}

const formatLongDate = function (value: Date | string, format = 'D. MMMM YYYY'): string {
    if (value) {
        return localizeMoment(String(value), format)
    }
    return '';
}

const formatDatetime = function (value: Date|string|number, format='D. MMMM YYYY'): string {
    if (value == undefined || value == null) return '';
    if (typeof(value) == 'number') value = new Date(value * 1000);
    if (typeof(value) != 'string') value = value.toISOString();

    if (value) {
        return localizeMoment(value, format)
    }
    
    return '';
}

const formatTime = function (value: Date|string|undefined, precision='minutes'): string|undefined {
    if (!value) return undefined;
    if (typeof(value) != 'string') value = value.toISOString();

    let format = "HH:mm";
    if (precision == "seconds") format = "HH:mm:ss";

    if (value) {
        return localizeMoment(value, format)
    }
    return '';
}

const calendar = function(value: string): string {
    return moment(value).calendar();
}

const secondsAgo = function(value: Date|string): number {
    if (!value) return 0;
    // Stored stamps are naive company-time (Europe/Berlin) wall-clock strings.
    // Express "now" in the same Berlin wall-clock before diffing — otherwise a
    // device set to another timezone (e.g. UK/Portugal) reads the stamp as
    // being in the future and reports a negative elapsed time.
    return moment(getLocaleDatetimeString(new Date())).diff(moment(String(value))) / 1000;
}

const _timeAgo = function(delta: number, json: boolean): string|{days: number, hours: number, minutes: number, seconds: number} {
    const days = Math.floor(delta / 86400);
    const hours = Math.floor((delta % 86400) / 3600);
    const minutes = Math.floor((delta % 3600) / 60);
    const seconds = Math.floor(delta % 60);

    if (json) {
        return {
            days,
            hours,
            minutes,
            seconds,
        }
    }

    let result = "";
    if (days > 0) result += days + "d ";
    if (hours > 0) result += hours + "h ";
    if (minutes > 0) result += minutes + "m ";
    result += seconds + "s";

    return result
}

const timeAgo = function(value: Date|string, json: boolean = false): string|{days: number, hours: number, minutes: number, seconds: number} {
    if (!value) return "";
    const delta = secondsAgo(value)
    return _timeAgo(delta, json)
}

const timeUntil =function(value: Date|string, json: boolean = false): string|{days: number, hours: number, minutes: number, seconds: number} {
    if (!value) return "";
    const delta = -secondsAgo(value)
    return _timeAgo(delta, json)
}

const toLocaleISOString = function(value: Date): string {
    return value.toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

const sortedJsonString = function(obj: any) {
    return JSON.stringify(obj, Object.keys(obj).sort());
}

const intToFootnote = function(idx: number): string {
  const charCount = 26;
  let result = '';
  result = String.fromCharCode(97 + idx % charCount);
  idx = Math.floor(idx / charCount);
  while (idx > 0) {
    result = String.fromCharCode(97 + (idx - 1) % charCount) + result;
    idx = Math.floor((idx - 1) / charCount);
  }
  return result;
}

function cleanTime(time: string, precision="seconds") {
  if (!time) return undefined;

  // Replace separators with :
  time = time.replace(/\./g, ":");
  time = time.replace(/,/g, ":");

  // If there is no separator in the time, add one after every two symbols
  if (time.indexOf(":") == -1) {
      time = (time.split("").reverse().join("").match(/.{1,2}/g) as Array<string>).join(":").split("").reverse().join("");
  }

  const timeParts = time.split(":");
  let hour = parseInt(timeParts[0]) || 0;
  let minute = timeParts.length > 1 ? (parseInt(timeParts[1]) || 0) : 0;
  let second = timeParts.length > 2 ? (parseInt(timeParts[2]) || 0) : 0;

  hour = Math.min(Math.max(0, hour), 23);
  minute = Math.min(Math.max(0, minute), 59);
  second = Math.min(Math.max(0, second), 59);

  let result = String(hour).padStart(2, "0") + ":" + String(minute).padStart(2, "0");
  if (precision == "seoncds") result += ":" + String(second).padStart(2, "0");

  return result
}

function cleanNumber(number: string): string {
    let newNumber = number.replace(",", '.').replace(/[^\d.-]/g, '');
    newNumber = String(Number(newNumber));
    if (newNumber == 'NaN') newNumber = '0';

    return (number == newNumber) ? number : newNumber
}

function formatNumber(number: number|string, unit?: string, fractionDigits: number = 2): string {
    let unitAttrs = {};
    if (unit) unitAttrs = {
        style: 'unit',
        unit,
    }

    // Avoid displaying "-0" for values that round to zero at the given precision
    const factor = Math.pow(10, fractionDigits);
    if (Math.round(Number(number) * factor) === 0) {
        number = 0;
    }

    return Intl.NumberFormat(
        getNumberFormat(),
        { maximumFractionDigits: fractionDigits, unitDisplay: 'long', ...unitAttrs }
    ).format(number)
}

function formatCurrency(number: number|string): string {
    let fractionDigits = 2;
    if (Math.floor(Number(number)) == Number(number)) {
        fractionDigits = 0;
    }

    return Intl.NumberFormat(
        getNumberFormat(),
        { maximumFractionDigits: fractionDigits, style: 'currency', currency: 'EUR' }
    ).format(number)
}

async function loadPaginatedResults<T>(service: BaseService<any>, responsePromise: Promise<AxiosResponse<PaginatedResponse<T>>>): Promise<Array<T>> {
    let entries = [] as Array<T>;
    let response = (await responsePromise);
    // The axios response interceptor returns undefined on Network Error (see src/axios.ts).
    if (!response) return entries;
    entries = entries.concat(response.data.results);

    while (response.data.next) {
        // The endpoint will be added in `service._get` again
        const nextPath = response.data.next.split(service.endpoint)[1];
        response = await (service._get(nextPath) as unknown as Promise<AxiosResponse<PaginatedResponse<T>>>);
        if (!response) return entries;
        entries = entries.concat(response.data.results);
    }

    return entries;
}

function monthRange(year: number, month: number): Array<string> {
    const startDate = new Date(Date.UTC(year, month - 1, 1)).toISOString().slice(0, 19);
    const endDate = new Date(Date.UTC(year, month, 1, 0, 0, -1)).toISOString().slice(0, 19);

    return [startDate, endDate]
}

function getDays(year: number, month: number) {
    const date = new Date(Date.UTC(year, month - 1, 1));
    const days = [];
    while (date.getMonth() === month - 1) {
        days.push((new Date(date)).toISOString().slice(0, 10));
        date.setUTCDate(date.getUTCDate() + 1);
    }
    return days;
}

function isAuthenticated() {
    const isDevice = (localStorage.accessMode == 'Device' && localStorage.deviceConfig);
    const isUser = localStorage.accessToken !== undefined;
    return isDevice || isUser;
}

function getLocaleDatetimeString(datetime?: Date) {
    const formatter = Intl.DateTimeFormat(['de'], {year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'Europe/Berlin'});
    const [date, time] = formatter.format(datetime).split(', ');
    const [day, month, year] = date.split('.');
    return `${year}-${month}-${day}T${time}`;
}

function localeDayNames(weekday = 'long') {
    const { format } = new Intl.DateTimeFormat(i18n.global.locale, { weekday });
    return [...Array(7).keys()]
        .map((day) => format(new Date(Date.UTC(2021, 5, day))));
}

function formatFileSize(bytes: number) {
    const limits = [
        { limit: 1000, divisor: 1, unit: 'B' },
        { limit: 1000 * 1000, divisor: 1000, unit: 'kB' },
        { limit: 1000 * 1000 * 1000, divisor: 1000 * 1000, unit: 'MB' },
        { limit: 1000 * 1000 * 1000 * 1000, divisor: 1000 * 1000 * 1000, unit: 'GB' },
    ]

    for (const limit of limits) {
        if (bytes < limit.limit) return formatNumber(bytes / limit.divisor, undefined, 1) + ' ' + limit.unit;
    }

    return formatNumber(bytes / 1000 / 1000 / 1000 / 1000, undefined, 1) + ' TB';
}
export { getTextColor, getNumberFormat, cleanTime, localeDayNames, intToFootnote, formatCurrency, formatDatetime, formatDifference, formatDifferenceV2, formatDuration, formatDurationV2, formatFileSize, formatLongDate, formatTime, timeAgo, timeUntil, getDays, calendar, sortedJsonString, formatDurationWithFormat, timedelta, cleanNumber, loadPaginatedResults, monthRange, isAuthenticated, getLocaleDatetimeString, secondsAgo, formatNumber, toLocaleISOString }
